import { useContext, useEffect } from "react";
import { useSWRConfig } from "swr";
import {
  GetNotifications200Response,
} from "../../codegen-api";
import { AuthContext } from "../../contexts/AuthContext";
import { WebsocketContext } from "../../contexts/WebsocketAuthContext";
import {
  isDisplayableNotification,
  notificationSWRKey,
} from "../api/notifications/useNotifications";
import { getNotificationDedupeKey } from "../../utils/notifications";
import { forEachFrame, readFrame } from "./parseFrame";
import { readNotificationFrame } from "./notificationFrames";

const ACTIVE_CHAIN_ID = String(process.env.REACT_APP_CHAIN_ID || "");

// Subscribes to the per-account notifications channel and pushes incoming
// notifications into the useNotifications SWR cache. Real-time updates flow:
//   backend NotificationService.create -> Socket.io emit -> this hook -> SWR mutate
// The diff-based useNewNotificationEffects then fires sound/toast/browser push.
//
// Only frames for the CURRENT account and chain are merged (FE-7): see
// readNotificationFrame.
const useNotificationsWSS = (enabled = true) => {
  const { account, apiKey } = useContext(AuthContext);
  const { authenticated, triggerSubscribe, triggerUnsubscribe, lastMessages } =
    useContext(WebsocketContext);
  const { mutate } = useSWRConfig();

  useEffect(() => {
    if (!enabled || !authenticated || !account) return undefined;
    const data = [`notifications:${account.toLowerCase()}`];
    triggerSubscribe("subscribe", data);
    return () => triggerUnsubscribe(data);
  }, [authenticated, enabled, account, triggerSubscribe, triggerUnsubscribe]);

  useEffect(() => {
    if (!enabled || !lastMessages || !apiKey || !account) return;

    forEachFrame(lastMessages, "useNotificationsWSS", (message) => {
      const frame = readNotificationFrame(
        readFrame(message),
        account,
        ACTIVE_CHAIN_ID
      );
      if (!frame) return;

      const swrKey = notificationSWRKey(apiKey);
      if (!swrKey) return;

      mutate(
        swrKey,
        (current: GetNotifications200Response[] | undefined) => {
          const list = current ?? [];

          // Snapshot delivered on subscribe — MERGE into the existing cache,
          // do NOT replace. The backend snapshot is capped at 20 rows while the
          // REST mount fetch loads up to 100; a blind replace truncated the list
          // to 20 on every reconnect/refresh (and wiped it entirely when the
          // snapshot filtered to empty), which is why notifications disappeared
          // on refresh. Merging keeps the fuller REST history and lets the
          // snapshot only add anything newer the REST fetch hadn't seen yet.
          if (frame.kind === "snapshot") {
            const snap = frame.items.filter(isDisplayableNotification);

            // Dedupe on the STABLE content key (tx_hash/fill_id), not the
            // volatile UUID, so a re-emitted copy of a row already in the list
            // doesn't get appended. Same key the live path below uses.
            const seen = new Set(list.map(getNotificationDedupeKey));
            const merged = [...list];
            for (const n of snap) {
              const key = getNotificationDedupeKey(n);
              if (seen.has(key)) continue;
              seen.add(key);
              merged.push(n);
            }

            // Newest first, capped to the same 100 the live path keeps.
            // created_timestamp is a nanosecond integer string; guard BigInt so a
            // single malformed value can't throw and abort the whole merge.
            const toBig = (s?: string) => {
              try {
                return BigInt(s || "0");
              } catch {
                return 0n;
              }
            };
            merged.sort((a, b) => {
              const at = toBig(a.created_timestamp);
              const bt = toBig(b.created_timestamp);
              return at > bt ? -1 : at < bt ? 1 : 0;
            });
            return merged.slice(0, 100);
          }

          const incoming = frame.item;
          if (!isDisplayableNotification(incoming)) {
            return list;
          }

          // Dedupe by STABLE content key (tx_hash/fill_id), not the volatile
          // UUID — the backend catch-up poller re-emits the same on-chain event
          // with a fresh id, which would otherwise pile day-old duplicates into
          // the list (and re-toast them). See getNotificationDedupeKey.
          const incomingKey = getNotificationDedupeKey(incoming);
          if (
            list.some((n) => getNotificationDedupeKey(n) === incomingKey)
          ) {
            return list;
          }

          return [incoming, ...list].slice(0, 100);
        },
        { revalidate: false }
      );
    });
  }, [enabled, lastMessages, apiKey, account, mutate]);
};

export default useNotificationsWSS;
